import "./styles/main.css";
import { getConfig } from "./config";
import { mappinoAttributionHtml } from "./services/attribution";
import { parseCoordinateQuery } from "./services/coordinates";
import { GeocodeClient } from "./services/geocode";
import {
  createLiveLocationController,
  isMobileViewport,
  type LiveLocationFix,
  type LiveLocationStatus,
} from "./services/liveLocation";
import { shouldExpandMobileSearch } from "./services/mobileSearch";
import {
  formatPersianAddress,
  formatPersianPlaceTitle,
  formatPersianPostcode,
  publicPlaceMeta,
} from "./services/placeFormatter";
import { shareView } from "./services/share";
import { loadMapStyle, standardStyle } from "./services/styleRegistry";
import { encodeMapState, parseMapState } from "./services/urlState";
import type { GeocodeError, MapViewState, Place } from "./types";

type ReverseMode = "idle" | "armed" | "loading" | "success" | "error" | "cancelled";
type StatusTone = "info" | "success" | "warning" | "error";

interface MapLibreApi {
  setRTLTextPlugin?: (url: string, callbackOrLazy?: boolean) => void | Promise<void>;
  Map: new (options: Record<string, unknown>) => MapInstance;
  NavigationControl: new () => unknown;
  Marker: new (options?: Record<string, unknown>) => MarkerInstance;
}

interface MapInstance {
  addControl(control: unknown, position?: string): void;
  on(event: string, handler: (event?: unknown) => void): void;
  getCenter(): { lat: number; lng: number };
  getZoom(): number;
  getPitch(): number;
  getBearing(): number;
  flyTo(options: Record<string, unknown>): void;
  setLayoutProperty(layerId: string, name: string, value: string): void;
  getLayer(layerId: string): unknown;
}

interface MarkerInstance {
  setLngLat(lngLat: [number, number]): MarkerInstance;
  addTo(map: MapInstance): MarkerInstance;
  remove(): void;
}

const fa = {
  app: "\u0645\u067e\u06cc\u0646\u0648",
  map: "\u0646\u0642\u0634\u0647",
  publicMap:
    "\u0646\u0642\u0634\u0647 \u0639\u0645\u0648\u0645\u06cc \u0645\u067e\u06cc\u0646\u0648",
  nav: "\u0646\u0627\u0648\u0628\u0631\u06cc \u0627\u0635\u0644\u06cc",
  docs: "\u0645\u0633\u062a\u0646\u062f\u0627\u062a",
  status: "\u0648\u0636\u0639\u06cc\u062a",
  menu: "\u0645\u0646\u0648",
  close: "\u0628\u0633\u062a\u0646",
  search: "\u062c\u0633\u062a\u062c\u0648",
  searchPlaceholder:
    "\u062c\u0633\u062a\u062c\u0648\u06cc \u0645\u06a9\u0627\u0646\u060c \u0622\u062f\u0631\u0633 \u06cc\u0627 \u0645\u062e\u062a\u0635\u0627\u062a",
  clearSearch: "\u067e\u0627\u06a9 \u06a9\u0631\u062f\u0646 \u062c\u0633\u062a\u062c\u0648",
  results: "\u0646\u062a\u0627\u06cc\u062c \u062c\u0633\u062a\u062c\u0648",
  tools: "\u0627\u0628\u0632\u0627\u0631\u0647\u0627\u06cc \u0646\u0642\u0634\u0647",
  locate: "\u0645\u0648\u0642\u0639\u06cc\u062a \u0645\u0646",
  reverse: "\u0627\u0646\u062a\u062e\u0627\u0628 \u0631\u0648\u06cc \u0646\u0642\u0634\u0647",
  share: "\u0627\u0634\u062a\u0631\u0627\u06a9\u200c\u06af\u0630\u0627\u0631\u06cc",
  layers: "\u0644\u0627\u06cc\u0647\u200c\u0647\u0627",
  layerSettings: "\u062a\u0646\u0638\u06cc\u0645 \u0644\u0627\u06cc\u0647\u200c\u0647\u0627",
  essentialLayers: "\u0644\u0627\u06cc\u0647\u200c\u0647\u0627\u06cc \u0627\u0635\u0644\u06cc",
  advancedLayers: "\u067e\u06cc\u0634\u0631\u0641\u062a\u0647",
  preparing:
    "\u062f\u0631 \u062d\u0627\u0644 \u0622\u0645\u0627\u062f\u0647\u200c\u0633\u0627\u0632\u06cc \u0646\u0642\u0634\u0647...",
  styleNote:
    "\u0628\u0631\u0686\u0633\u0628\u200c\u0647\u0627\u06cc \u0641\u0627\u0631\u0633\u06cc \u0646\u0642\u0634\u0647 \u0648\u0627\u0628\u0633\u062a\u0647 \u0628\u0647 \u0627\u0646\u062a\u0634\u0627\u0631 \u0633\u0628\u06a9 \u062a\u0623\u06cc\u06cc\u062f\u0634\u062f\u0647 \u0627\u0633\u062a.",
  searching: "\u062f\u0631 \u062d\u0627\u0644 \u062c\u0633\u062a\u062c\u0648...",
  noResults:
    "\u0646\u062a\u06cc\u062c\u0647\u200c\u0627\u06cc \u067e\u06cc\u062f\u0627 \u0646\u0634\u062f.",
  ready: "\u0646\u0642\u0634\u0647 \u0622\u0645\u0627\u062f\u0647 \u0627\u0633\u062a.",
  mapError:
    "\u0628\u062e\u0634\u06cc \u0627\u0632 \u0646\u0642\u0634\u0647 \u062f\u0631 \u062f\u0633\u062a\u0631\u0633 \u0646\u06cc\u0633\u062a.",
  unavailable:
    "\u0646\u0642\u0634\u0647 \u062f\u0631 \u062f\u0633\u062a\u0631\u0633 \u0646\u06cc\u0633\u062a.",
  coordinate: "\u0645\u062e\u062a\u0635\u0627\u062a",
  coordinateFound:
    "\u0645\u062e\u062a\u0635\u0627\u062a \u0634\u0646\u0627\u0633\u0627\u06cc\u06cc \u0634\u062f.",
  kind: "\u0646\u0648\u0639",
  confidence: "\u0627\u0637\u0645\u06cc\u0646\u0627\u0646",
  copyCoordinate: "\u06a9\u067e\u06cc \u0645\u062e\u062a\u0635\u0627\u062a",
  centerMap: "\u0645\u0631\u06a9\u0632 \u0646\u0642\u0634\u0647",
  chooseOnMap:
    "\u0631\u0648\u06cc \u0646\u0642\u0634\u0647 \u0627\u0646\u062a\u062e\u0627\u0628 \u06a9\u0646\u06cc\u062f.",
  reversing:
    "\u062f\u0631 \u062d\u0627\u0644 \u06cc\u0627\u0641\u062a\u0646 \u0622\u062f\u0631\u0633...",
  reverseDone:
    "\u0646\u062a\u06cc\u062c\u0647 \u0627\u0646\u062a\u062e\u0627\u0628 \u0631\u0648\u06cc \u0646\u0642\u0634\u0647 \u0622\u0645\u0627\u062f\u0647 \u0627\u0633\u062a.",
  denied:
    "\u062f\u0633\u062a\u0631\u0633\u06cc \u0628\u0647 \u0645\u0648\u0642\u0639\u06cc\u062a \u062f\u0627\u062f\u0647 \u0646\u0634\u062f.",
  locating:
    "\u062f\u0631 \u062d\u0627\u0644 \u06cc\u0627\u0641\u062a\u0646 \u0645\u0648\u0642\u0639\u06cc\u062a \u0634\u0645\u0627...",
  locationActive:
    "\u0645\u0648\u0642\u0639\u06cc\u062a \u0632\u0646\u062f\u0647 \u0641\u0639\u0627\u0644 \u0627\u0633\u062a.",
  locationUnavailable:
    "\u0645\u0648\u0642\u0639\u06cc\u062a \u062f\u0631 \u062f\u0633\u062a\u0631\u0633 \u0646\u06cc\u0633\u062a.",
  locationTimeout:
    "\u06cc\u0627\u0641\u062a\u0646 \u0645\u0648\u0642\u0639\u06cc\u062a \u0637\u0648\u0644 \u06a9\u0634\u06cc\u062f.",
  yourLocation: "\u0645\u0648\u0642\u0639\u06cc\u062a \u0641\u0639\u0644\u06cc \u0634\u0645\u0627",
  noGeo:
    "\u0645\u0631\u0648\u0631\u06af\u0631 \u0645\u0648\u0642\u0639\u06cc\u062a\u200c\u06cc\u0627\u0628\u06cc \u0631\u0627 \u067e\u0634\u062a\u06cc\u0628\u0627\u0646\u06cc \u0646\u0645\u06cc\u200c\u06a9\u0646\u062f.",
  linkReady:
    "\u067e\u06cc\u0648\u0646\u062f \u0646\u0642\u0634\u0647 \u0622\u0645\u0627\u062f\u0647 \u0634\u062f.",
  requestError:
    "\u062f\u0631\u062e\u0648\u0627\u0633\u062a \u0628\u0627 \u062e\u0637\u0627 \u0631\u0648\u0628\u0647\u200c\u0631\u0648 \u0634\u062f.",
  experimental: "\u0622\u0632\u0645\u0627\u06cc\u0634\u06cc",
};

const config = getConfig();
const geocode = new GeocodeClient({ basePath: config.geocodeBasePath });
let map: MapInstance | null = null;
let selectedMarker: MarkerInstance | null = null;
let userLocationMarker: MarkerInstance | null = null;
let searchAbort: AbortController | null = null;
let reverseAbort: AbortController | null = null;
let highlightedIndex = -1;
let currentResults: Place[] = [];
let reverseMode: ReverseMode = "idle";
let statusTimer: number | undefined;
let layersOpen = false;
let searchFocused = false;
let searchLoading = false;
let searchCollapsedAfterSelect = false;

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("missing #app");

app.innerHTML = `
  <main class="maps-shell" aria-label="${fa.publicMap}">
    <div id="map" class="map-canvas" aria-label="${fa.map}"></div>
    <header class="top-chrome" aria-label="${fa.nav}">
      <a class="brand" href="/" aria-label="${fa.publicMap}">${fa.app}</a>
      <nav class="desktop-nav" aria-label="${fa.nav}">
        <a href="/doc/">${fa.docs}</a>
        <a href="/status/">${fa.status}</a>
      </nav>
      <details class="mobile-nav">
        <summary aria-label="${fa.menu}">${fa.menu}</summary>
        <nav>
          <a href="/doc/">${fa.docs}</a>
          <a href="/status/">${fa.status}</a>
        </nav>
      </details>
    </header>
    <section class="search-panel" aria-label="${fa.search}">
      <div class="search-box" data-icon="search">
        <label class="sr-only" for="search-input">${fa.search}</label>
        <input
          id="search-input"
          autocomplete="off"
          placeholder="${fa.searchPlaceholder}"
          role="combobox"
          aria-autocomplete="list"
          aria-controls="results"
          aria-expanded="false"
          aria-haspopup="listbox"
        />
        <button id="clear-search" class="clear-search" type="button" aria-label="${fa.clearSearch}" hidden>
          ${icon("close")}
        </button>
      </div>
      <div id="search-state" class="search-state" role="status"></div>
      <div id="results" class="results" role="listbox" aria-label="${fa.results}"></div>
    </section>
    <article id="place-card" class="place-card" hidden></article>
    <div class="map-tools" aria-label="${fa.tools}">
      <button id="locate-btn" type="button" aria-label="${fa.locate}">${icon("locate")}</button>
      <button id="reverse-btn" type="button" aria-label="${fa.reverse}" aria-pressed="false">
        ${icon("pin")}
      </button>
      <button id="share-btn" type="button" aria-label="${fa.share}">${icon("share")}</button>
      <button
        id="layers-btn"
        type="button"
        aria-label="${fa.layerSettings}"
        aria-controls="layer-panel"
        aria-expanded="false"
      >
        ${icon("layers")}
      </button>
    </div>
    <aside id="layer-panel" class="layer-panel" aria-label="${fa.layers}" hidden>
      <div class="layer-panel-header">
        <div>
          <strong>${fa.layers}</strong>
          <span>${fa.layerSettings}</span>
        </div>
        <button id="layers-close" type="button" aria-label="${fa.close}">${icon("close")}</button>
      </div>
      <div id="layers"></div>
      <p class="launch-note">${fa.styleNote}</p>
    </aside>
    <div id="status-toast" class="map-status" role="status" aria-live="polite">
      <span id="status-text">${fa.preparing}</span>
    </div>
    <div class="mappino-attribution" aria-label="Map attribution">${mappinoAttributionHtml}</div>
  </main>
`;

const searchInput = must<HTMLInputElement>("#search-input");
const searchState = must<HTMLDivElement>("#search-state");
const resultsEl = must<HTMLDivElement>("#results");
const placeCard = must<HTMLElement>("#place-card");
const statusText = must<HTMLSpanElement>("#status-text");
const statusToast = must<HTMLDivElement>("#status-toast");
const layersEl = must<HTMLDivElement>("#layers");
const reverseBtn = must<HTMLButtonElement>("#reverse-btn");
const layersBtn = must<HTMLButtonElement>("#layers-btn");
const layerPanel = must<HTMLElement>("#layer-panel");
const clearSearchBtn = must<HTMLButtonElement>("#clear-search");
const searchPanel = must<HTMLElement>(".search-panel");

const liveLocation = createLiveLocationController(navigator.geolocation, {
  onUpdate: updateUserLocationMarker,
  onStatus: handleLocationStatus,
});

renderLayerControls();
updateSearchExpanded();
void bootMap();

searchInput.addEventListener("input", debounceSearch);
searchInput.addEventListener("focus", () => {
  searchFocused = true;
  searchCollapsedAfterSelect = false;
  updateSearchExpanded();
});
searchInput.addEventListener("blur", () => {
  searchFocused = false;
  updateSearchExpanded();
});
searchInput.addEventListener("keydown", handleSearchKeydown);
document.addEventListener("keydown", (event) => {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
    event.preventDefault();
    searchInput.focus();
  }
  if (event.key === "Escape") {
    cancelReverseMode();
    closeLayers();
    collapseSearch();
  }
});
clearSearchBtn.addEventListener("click", () => {
  window.clearTimeout(searchTimer);
  searchAbort?.abort();
  searchAbort = null;
  searchInput.value = "";
  collapseSearch();
  searchInput.blur();
});
must<HTMLButtonElement>("#locate-btn").addEventListener("click", recenterOnUser);
must<HTMLButtonElement>("#share-btn").addEventListener("click", () => {
  if (!map) return;
  void shareView(readMapState()).then(() => setStatus(fa.linkReady, "success", true));
});
reverseBtn.addEventListener("click", armReverseMode);
layersBtn.addEventListener("click", toggleLayers);
must<HTMLButtonElement>("#layers-close").addEventListener("click", closeLayers);
window.addEventListener("pagehide", stopLiveLocation);
window.addEventListener("beforeunload", stopLiveLocation);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    stopLiveLocation();
    return;
  }
  if (isMobileViewport()) startLiveLocation(true);
});

async function bootMap(): Promise<void> {
  try {
    await waitForMapLibre();
    const maplibre = window.maplibregl as MapLibreApi;
    if (typeof maplibre.setRTLTextPlugin === "function") {
      try {
        await maplibre.setRTLTextPlugin(config.rtlPluginUrl, false);
      } catch (error) {
        console.warn("rtl_plugin_unavailable", error);
      }
    }
    const initialView = parseMapState(window.location.hash) ?? config.defaultView;
    const style = await loadMapStyle(
      config.styleUrl,
      config.tileUrlTemplate,
      config.glyphUrlTemplate,
    );
    map = new maplibre.Map({
      container: "map",
      style,
      center: [initialView.lon, initialView.lat],
      zoom: initialView.zoom,
      pitch: initialView.pitch,
      bearing: initialView.bearing,
      attributionControl: false,
      transformRequest: (url: string) => {
        if (/nominatim|martin|postgres|postgis/i.test(url)) {
          throw new Error("direct_internal_access_blocked");
        }
        return { url };
      },
    });
    map.addControl(new maplibre.NavigationControl(), "top-left");
    map.on("load", () => {
      setStatus(fa.ready, "success", true);
      if (isMobileViewport()) startLiveLocation(true);
    });
    map.on("error", () => setStatus(fa.mapError, "error"));
    map.on("moveend", updateHash);
    map.on("click", (event) => {
      void handleMapClick(event as { lngLat?: { lat: number; lng: number } });
    });
  } catch (error) {
    setStatus(error instanceof Error ? error.message : fa.unavailable, "error");
  }
}

let searchTimer: number | undefined;
function debounceSearch(): void {
  searchCollapsedAfterSelect = false;
  clearSearchBtn.hidden = searchInput.value.length === 0;
  window.clearTimeout(searchTimer);
  searchTimer = window.setTimeout(() => void runSearch(), 280);
  updateSearchExpanded();
}

async function runSearch(): Promise<void> {
  const query = searchInput.value.trim();
  searchAbort?.abort();
  clearResults();
  clearSearchBtn.hidden = !query;
  if (!query) return;
  const coordinates = parseCoordinateQuery(query);
  if (coordinates) {
    showCoordinateResult(coordinates.lat, coordinates.lon);
    return;
  }
  const controller = new AbortController();
  searchAbort = controller;
  searchLoading = true;
  searchState.textContent = fa.searching;
  syncSearchAria();
  try {
    const response = await geocode.search(query, controller.signal);
    currentResults = response.results;
    if (currentResults.length === 0) {
      searchState.textContent = fa.noResults;
      syncSearchAria();
      return;
    }
    searchState.textContent = `${currentResults.length} ${fa.results}`;
    renderResults(currentResults);
    syncSearchAria();
  } catch (error) {
    if (controller.signal.aborted) return;
    searchState.textContent = describeError(error);
    syncSearchAria();
  } finally {
    searchLoading = false;
    if (searchAbort === controller) {
      searchAbort = null;
    }
    updateSearchExpanded();
  }
}

function renderResults(results: Place[]): void {
  resultsEl.replaceChildren(
    ...results.map((place, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.id = resultOptionId(index);
      button.className = "result-row";
      button.setAttribute("role", "option");
      button.setAttribute("aria-selected", String(index === highlightedIndex));
      button.append(resultTitle(formatPersianPlaceTitle(place)), resultMeta(place));
      button.addEventListener("click", () => {
        highlightedIndex = index;
        renderResults(currentResults);
        syncSearchAria();
        selectPlace(place);
      });
      if (index === highlightedIndex) button.classList.add("is-active");
      return button;
    }),
  );
}

function handleSearchKeydown(event: KeyboardEvent): void {
  if (event.key === "Escape") {
    event.preventDefault();
    collapseSearch();
    searchInput.blur();
    return;
  }
  if (!currentResults.length) return;
  if (event.key === "ArrowDown") {
    event.preventDefault();
    highlightedIndex = Math.min(currentResults.length - 1, highlightedIndex + 1);
    renderResults(currentResults);
    syncSearchAria();
  }
  if (event.key === "ArrowUp") {
    event.preventDefault();
    highlightedIndex = Math.max(0, highlightedIndex - 1);
    renderResults(currentResults);
    syncSearchAria();
  }
  if (event.key === "Enter" && highlightedIndex >= 0) {
    event.preventDefault();
    selectPlace(currentResults[highlightedIndex]);
  }
}

function showCoordinateResult(lat: number, lon: number): void {
  const place: Place = {
    place_id: `coord:${lat},${lon}`,
    display_name: `${fa.coordinate} ${lat.toFixed(5)}, ${lon.toFixed(5)}`,
    lat,
    lon,
    confidence: 1,
    kind: "coordinate",
  };
  currentResults = [place];
  searchState.textContent = fa.coordinateFound;
  renderResults(currentResults);
  syncSearchAria();
}

function selectPlace(place: Place): void {
  selectedMarker?.remove();
  const maplibre = window.maplibregl as MapLibreApi;
  selectedMarker = new maplibre.Marker({ color: "#0f766e" }).setLngLat([place.lon, place.lat]);
  if (map) {
    selectedMarker.addTo(map);
    map.flyTo({
      center: [place.lon, place.lat],
      zoom: Math.max(15, map.getZoom()),
      essential: true,
    });
  }
  renderPlaceCard(place);
  searchCollapsedAfterSelect = true;
  clearResults();
  searchInput.blur();
  syncSearchAria();
}

function renderPlaceCard(place: Place): void {
  placeCard.hidden = false;
  placeCard.textContent = "";
  placeCard.classList.remove("is-expanded");
  placeCard.addEventListener("click", expandPlaceCard, { once: true });
  const handle = document.createElement("button");
  handle.className = "place-card-handle";
  handle.type = "button";
  handle.setAttribute("aria-label", "نمایش جزئیات مکان");
  handle.addEventListener("click", (event) => {
    event.stopPropagation();
    expandPlaceCard();
  });
  const title = document.createElement("h2");
  title.textContent = formatPersianPlaceTitle(place);
  const address = document.createElement("p");
  address.className = "place-address";
  address.textContent = formatPersianAddress(place);
  const postcode = document.createElement("p");
  postcode.className = "place-postcode";
  const formattedPostcode = formatPersianPostcode(place.address);
  postcode.textContent = formattedPostcode ? `کدپستی: ${formattedPostcode}` : "";
  postcode.hidden = !formattedPostcode;
  const meta = document.createElement("p");
  meta.className = "place-meta";
  meta.textContent = publicPlaceMeta(place);
  const actions = document.createElement("div");
  actions.className = "card-actions";
  actions.append(
    button(fa.copyCoordinate, () => {
      void navigator.clipboard.writeText(`${place.lat.toFixed(6)}, ${place.lon.toFixed(6)}`);
    }),
    button(fa.centerMap, () => selectPlace(place)),
  );
  placeCard.append(handle, title, address, postcode, meta, actions);
}

function armReverseMode(): void {
  reverseMode = "armed";
  reverseBtn.classList.add("is-active");
  reverseBtn.setAttribute("aria-pressed", "true");
  setStatus(fa.chooseOnMap, "info");
}

function cancelReverseMode(): void {
  if (reverseMode === "idle") return;
  reverseAbort?.abort();
  reverseMode = "cancelled";
  reverseBtn.classList.remove("is-active");
  reverseBtn.setAttribute("aria-pressed", "false");
  setStatus("");
}

async function handleMapClick(event: { lngLat?: { lat: number; lng: number } }): Promise<void> {
  if (reverseMode !== "armed" || !event.lngLat) return;
  reverseMode = "loading";
  reverseAbort?.abort();
  reverseAbort = new AbortController();
  setStatus(fa.reversing, "info");
  try {
    const response = await geocode.reverse(event.lngLat.lat, event.lngLat.lng, reverseAbort.signal);
    const place =
      response.results[0] ??
      ({
        place_id: `reverse:${event.lngLat.lat},${event.lngLat.lng}`,
        display_name: fa.coordinate,
        lat: event.lngLat.lat,
        lon: event.lngLat.lng,
        confidence: 0,
        kind: "coordinate",
      } satisfies Place);
    reverseMode = "success";
    reverseBtn.classList.remove("is-active");
    reverseBtn.setAttribute("aria-pressed", "false");
    selectPlace(place);
    setStatus(fa.reverseDone, "success", true);
  } catch (error) {
    if (reverseAbort.signal.aborted) return;
    reverseMode = "error";
    reverseBtn.classList.remove("is-active");
    reverseBtn.setAttribute("aria-pressed", "false");
    setStatus(describeError(error), "error");
  }
}

function startLiveLocation(auto = false): void {
  if (!navigator.geolocation) {
    setStatus(fa.noGeo, "warning");
    return;
  }
  liveLocation.start(auto);
}

function stopLiveLocation(): void {
  liveLocation.stop();
}

function recenterOnUser(): void {
  liveLocation.recenter();
}

function updateUserLocationMarker(fix: LiveLocationFix, shouldRecenter: boolean): void {
  const maplibre = window.maplibregl as MapLibreApi;
  if (!userLocationMarker) {
    const marker = document.createElement("div");
    marker.className = "user-location-marker";
    marker.setAttribute("role", "img");
    marker.setAttribute("aria-label", fa.yourLocation);
    marker.title = fa.yourLocation;
    userLocationMarker = new maplibre.Marker({ element: marker }).setLngLat([fix.lon, fix.lat]);
    if (map) userLocationMarker.addTo(map);
  } else {
    userLocationMarker.setLngLat([fix.lon, fix.lat]);
  }
  if (fix.accuracy) {
    document
      .querySelector<HTMLElement>(".user-location-marker")
      ?.style.setProperty("--accuracy-size", `${Math.min(Math.max(fix.accuracy / 2, 34), 96)}px`);
  }
  if (shouldRecenter) {
    map?.flyTo({ center: [fix.lon, fix.lat], zoom: Math.max(15, map.getZoom()), essential: true });
  }
}

function handleLocationStatus(status: LiveLocationStatus): void {
  const messages: Record<LiveLocationStatus, [string, StatusTone, boolean]> = {
    idle: ["", "info", false],
    starting: [fa.locating, "info", false],
    active: [fa.locationActive, "success", true],
    denied: [fa.denied, "warning", false],
    unavailable: [fa.locationUnavailable, "warning", false],
    timeout: [fa.locationTimeout, "warning", false],
  };
  const [message, tone, autoHide] = messages[status];
  setStatus(message, tone, autoHide);
}

function renderLayerControls(): void {
  const essential = document.createElement("section");
  const advanced = document.createElement("section");
  essential.className = "layer-group";
  advanced.className = "layer-group";
  essential.append(sectionTitle(fa.essentialLayers));
  advanced.append(sectionTitle(fa.advancedLayers));

  for (const group of standardStyle.layerGroups) {
    const label = document.createElement("label");
    label.className = "layer-toggle";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = group.defaultVisible && group.readiness !== "disabled";
    input.disabled = group.readiness === "disabled";
    input.addEventListener("change", () => {
      if (!map) return;
      for (const id of group.styleLayerIds) {
        if (map.getLayer(id)) {
          map.setLayoutProperty(id, "visibility", input.checked ? "visible" : "none");
        }
      }
    });
    const text = document.createElement("span");
    text.textContent =
      group.readiness === "experimental" ? `${group.label} (${fa.experimental})` : group.label;
    label.append(input, text);
    if (group.readiness === "experimental") {
      advanced.append(label);
    } else {
      essential.append(label);
    }
  }

  layersEl.replaceChildren(essential, advanced);
}

function readMapState(): MapViewState {
  if (!map) return config.defaultView;
  const center = map.getCenter();
  return {
    lat: center.lat,
    lon: center.lng,
    zoom: map.getZoom(),
    pitch: map.getPitch(),
    bearing: map.getBearing(),
  };
}

function updateHash(): void {
  window.history.replaceState(null, "", encodeMapState(readMapState()));
}

function clearResults(): void {
  highlightedIndex = -1;
  currentResults = [];
  resultsEl.textContent = "";
  searchState.textContent = "";
  clearSearchBtn.hidden = searchInput.value.length === 0;
  syncSearchAria();
}

function collapseSearch(): void {
  searchCollapsedAfterSelect = true;
  searchFocused = false;
  searchLoading = false;
  clearResults();
}

function setStatus(message: string, tone: StatusTone = "info", autoHide = false): void {
  window.clearTimeout(statusTimer);
  statusText.textContent = message;
  statusToast.dataset.tone = tone;
  statusToast.hidden = !message;
  if (message && autoHide) {
    statusTimer = window.setTimeout(() => setStatus(""), 2800);
  }
}

function describeError(error: unknown): string {
  const geocodeError = error as Partial<GeocodeError>;
  return geocodeError.message ?? fa.requestError;
}

function button(label: string, onClick: () => void): HTMLButtonElement {
  const element = document.createElement("button");
  element.type = "button";
  element.textContent = label;
  element.addEventListener("click", onClick);
  return element;
}

function resultTitle(text: string): HTMLSpanElement {
  const title = document.createElement("span");
  title.className = "result-title";
  title.textContent = text;
  return title;
}

function resultMeta(place: Place): HTMLSpanElement {
  const meta = document.createElement("span");
  meta.className = "result-meta";
  meta.textContent = publicPlaceMeta(place);
  return meta;
}

function resultOptionId(index: number): string {
  return `search-result-${index}`;
}

function sectionTitle(text: string): HTMLHeadingElement {
  const heading = document.createElement("h2");
  heading.textContent = text;
  return heading;
}

function toggleLayers(): void {
  if (layersOpen) {
    closeLayers();
    return;
  }
  layersOpen = true;
  layerPanel.hidden = false;
  layersBtn.classList.add("is-active");
  layersBtn.setAttribute("aria-expanded", "true");
}

function closeLayers(): void {
  layersOpen = false;
  layerPanel.hidden = true;
  layersBtn.classList.remove("is-active");
  layersBtn.setAttribute("aria-expanded", "false");
}

function syncSearchAria(): void {
  if (highlightedIndex >= 0 && currentResults[highlightedIndex]) {
    searchInput.setAttribute("aria-activedescendant", resultOptionId(highlightedIndex));
  } else {
    searchInput.removeAttribute("aria-activedescendant");
  }
  updateSearchExpanded();
}

function updateSearchExpanded(): void {
  const expanded =
    shouldExpandMobileSearch({
      focused: searchFocused,
      query: searchInput.value,
      loading: searchLoading,
      resultCount: currentResults.length,
    }) && !searchCollapsedAfterSelect;
  searchInput.setAttribute("aria-expanded", String(expanded));
  searchPanel.classList.toggle("is-expanded", expanded);
  searchPanel.classList.toggle("is-compact", !expanded);
}

function expandPlaceCard(): void {
  placeCard.classList.add("is-expanded");
}

function icon(name: "close" | "layers" | "locate" | "pin" | "share"): string {
  const paths: Record<typeof name, string> = {
    close: '<path d="M6 6l12 12M18 6L6 18" />',
    layers:
      '<path d="M12 3l9 5-9 5-9-5 9-5z" /><path d="M3 12l9 5 9-5" /><path d="M3 16l9 5 9-5" />',
    locate:
      '<path d="M12 2v3M12 19v3M2 12h3M19 12h3" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" />',
    pin: '<path d="M12 21s6-5.2 6-11a6 6 0 10-12 0c0 5.8 6 11 6 11z" /><circle cx="12" cy="10" r="2" />',
    share:
      '<path d="M14 4h6v6" /><path d="M20 4l-9 9" /><path d="M18 13v5a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h5" />',
  };
  return `<svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">${paths[name]}</svg>`;
}

function must<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`missing element ${selector}`);
  return element;
}

async function waitForMapLibre(): Promise<void> {
  const started = Date.now();
  while (!window.maplibregl) {
    if (Date.now() - started > 8000) throw new Error("MapLibre runtime unavailable");
    await new Promise((resolve) => window.setTimeout(resolve, 50));
  }
}
